import axios from 'axios';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000/api",
    timeout: 30000,
    withCredentials: false, // IMPORTANT for token auth
});

// ─────────────────────────────
// ERROR NORMALIZER (IMPORTANT)
// ─────────────────────────────
function normalizeError(error) {
    if (error.response) {
        const { status, data } = error.response;

        return new Error(
            data?.message ||
            data?.error ||
            `Request failed with status ${status}`
        );
    }

    if (error.request) {
        console.error("No response received:", error.request);
        return new Error("No response from server. Possible CORS or timeout issue.");
    }

    return new Error(error.message || "Unexpected error occurred.");

}

// ─────────────────────────────
// TOKEN STORAGE LAYER
// ─────────────────────────────
export const TokenService = {
    getToken: () => sessionStorage.getItem("ACCESS_TOKEN"),

    setToken: (token) => {
        sessionStorage.setItem("ACCESS_TOKEN", token);
    },

    clear: () => {
        sessionStorage.removeItem("ACCESS_TOKEN");
    },
};

// ─────────────────────────────
// REQUEST INTERCEPTOR
// ─────────────────────────────
api.interceptors.request.use(
    (config) => {
        const token = TokenService.getToken();

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        config.headers.Accept = "application/json";
        return config;
    },
    (error) => Promise.reject(error)
);

// ─────────────────────────────
// RESPONSE INTERCEPTOR
// ─────────────────────────────
let isLoggingOut = false;

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const normalizedError = normalizeError(error);

        if (!error.response) {
            return Promise.reject(normalizedError);
        }

        const status = error.response.status;

        const RETRY_KEY = "__retry";

        if (status === 401 && !originalRequest[RETRY_KEY]) {
            originalRequest[RETRY_KEY] = true;

            if (!isLoggingOut) {
                isLoggingOut = true;

                TokenService.clear();
                window.location.href = "/pages/login/login3";
            }
        }

        return Promise.reject(normalizedError);
    }
);


export default api;


const qs = (params = {}) => {
    const p = Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''));
    return Object.keys(p).length ? `?${new URLSearchParams(p).toString()}` : '';
};

export const MainDashboardApi = {
    fetchFinancialYears: () => api.get('/financial-years').then((r) => r.data),
    fetchDashboardValues: (fyId) => api.get(`/values${qs({ financial_year_id: fyId })}`).then((r) => r.data),
    fetchBarChart: (fyId) => api.get(`/bar-chart${qs({ financial_year_id: fyId })}`).then((r) => r.data),
    fetchDashboardSummary: (fyId) => api.get(`/summary${qs({ financial_year_id: fyId })}`).then((r) => r.data),
    fetchNewReports: () => api.get('/new-reports').then((r) => r.data),
    fetchNewUsers: () => api.get('/new-users').then((r) => r.data),
    fetchMapProjects: (fyId, filters) => api.get(`/map-projects${qs({ financial_year_id: fyId, ...filters })}`).then((r) => r.data.data),
    fetchDougnutChart: (fyId) => api.get(`/doughnut-chart${qs({ financial_year_id: fyId })}`).then((r) => r.data),

    // ── Reports ───────────────────────────────────────────────────────────────
    fetchExecutiveSummary: (fyId) => api.get(`/reports/executive-summary${qs({ financial_year_id: fyId })}`).then((r) => r.data),
    fetchProjectsReport: (fyId, filters) => api.get(`/reports/projects${qs({ financial_year_id: fyId, ...filters })}`).then((r) => r.data),
    fetchFinancialReport: (fyId) => api.get(`/reports/financial${qs({ financial_year_id: fyId })}`).then((r) => r.data),
    fetchCertificatesReport: (fyId, status) => api.get(`/reports/certificates${qs({ financial_year_id: fyId, status })}`).then((r) => r.data),
    fetchAuditReport: (fyId, filters) => api.get(`/reports/audit${qs({ financial_year_id: fyId, ...filters })}`).then((r) => r.data),
    auditLogs: (filters) => api.get('/audit_logs', { params: filters }),

};

export const authApi = {
    updateToken: (token) => api.patch('/token', { fcm_token: token }),
    refreshToken: () => api.post('/refresh-token').then((r) => r.data)
};

export const yearApi = {
    fetchFinancialYears: () => api.get('/financial-years').then((r) => r.data),
    fetchFinancialYear: (id) => api.get(`/financial-years/${id}`).then((r) => r.data),
    createFinancialYear: (payload) => api.post('/financial-years', payload).then((r) => r.data),
    activateFinancialYear: (id) => api.post(`/financial-years/${id}/activate`).then((r) => r.data),
    deleteFinancialYear: (id) => api.delete(`/financial-years/${id}`).then((r) => r.data),
    fetchPendingBudgets: (id) => api.get(`/financial-years/${id}/pending-budgets`).then((r) => r.data)
}

// -----------Funds API---------------------
export const fundApi = {
    dashboardFunds: () => api.get('/all-funds'),
    allFunds: () => api.get('/funds'),
    fetchFundBudgets: (fundId) => api.get(`/funds/${fundId}/budgets`).then((r) => r.data),
    fetchFundAvailableYears: (fundId) => api.get(`/funds/${fundId}/available-years`).then((r) => r.data),
    createFundBudget: ({ fundId, payload }) => api.post(`/funds/${fundId}/budgets`, payload).then((r) => r.data),
    updateFundBudget: ({ fundId, budgetId, payload }) => api.put(`/funds/${fundId}/budgets/${budgetId}`, payload).then((r) => r.data),
    deleteFundBudget: ({ fundId, budgetId }) => api.delete(`/funds/${fundId}/budgets/${budgetId}`).then((r) => r.data),
};
export const sectorsApi = {
    allSectors: () => api.get('/sectors'),
    fetchSectorBudgets: (sectorId) => api.get(`/sectors/${sectorId}/budgets`).then((r) => r.data),
    fetchSectorAvailableYears: (sectorId) => api.get(`/sectors/${sectorId}/available-years`).then((r) => r.data),
    createSectorBudget: ({ sectorId, payload }) => api.post(`/sectors/${sectorId}/budgets`, payload).then((r) => r.data),
    updateSectorBudget: ({ sectorId, budgetId, payload }) => api.put(`/sectors/${sectorId}/budgets/${budgetId}`, payload).then((r) => r.data),
    deleteSectorBudget: ({ sectorId, budgetId }) => api.delete(`/sectors/${sectorId}/budgets/${budgetId}`).then((r) => r.data),
};

// ── Users API ─────────────────────────────────────────────────────────────────
export const usersApi = {
    allUsers: () => api.get('/all_users'),
    list: (params) => api.get('/admin/users', { params }).then((r) => r.data),
    get: (id) => api.get(`/admin/users/${id}`).then((r) => r.data),
    create: (data) => api.post('/admin/users', data).then((r) => r.data),
    update: (id, data) => api.put(`/admin/users/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/admin/users/${id}`).then((r) => r.data),

    assignRoles: (id, data) => api.post(`/admin/users/${id}/roles`, data).then((r) => r.data),
    removeRole: (id, roleId) => api.delete(`/admin/users/${id}/roles/${roleId}`).then((r) => r.data),
    setPermission: (id, data) => api.post(`/admin/users/${id}/permissions`, data).then((r) => r.data),
    removePermission: (id, permId) => api.delete(`/admin/users/${id}/permissions/${permId}`).then((r) => r.data),
    assignDepartment: (id, data) => api.patch(`/admin/users/${id}/department`, data).then((r) => r.data),
    toggleActive: (id) => api.patch(`/admin/users/${id}/toggle-active`).then((r) => r.data),
    getStats: (id) => api.get(`/admin/users/${id}/stats`)
};

// ── Departments API ───────────────────────────────────────────────────────────
export const departmentsApi = {
    dropDown: () => api.get('/departments/dropdown').then((r) => r.data),
    list: (params) => api.get('/admin/departments', { params }).then((r) => r.data),
    create: (data) => api.post('/admin/departments', data).then((r) => r.data),
    update: (id, data) => api.put(`/admin/departments/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/admin/departments/${id}`).then((r) => r.data),
    setHead: (id, userId) => api.patch(`/admin/departments/${id}/set-head`, { user_id: userId }).then((r) => r.data)
};

// ── Roles & Permissions API ───────────────────────────────────────────────────
export const rolesApi = {
    list: () => api.get('/admin/roles').then((r) => r.data),
    allPermissions: () => api.get('/admin/permissions').then((r) => r.data),
    create: (data) => api.post('/admin/roles', data).then((r) => r.data),
    update: (id, data) => api.put(`/admin/roles/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/admin/roles/${id}`).then((r) => r.data),
    syncPermissions: (id, data) => api.put(`/admin/roles/${id}/permissions`, data).then((r) => r.data)
};

// ── Audit Logs API ────────────────────────────────────────────────────────────
export const auditApi = {
    list: (params) => api.get('/admin/audit-logs', { params }).then((r) => r.data),
    export: (params) => api.get('/admin/audit-logs/export', { params, responseType: 'blob' }).then((r) => r.data),
    modules: () => api.get('/admin/audit-logs/modules').then((r) => r.data)
};

// -----------Contractors API---------------------
export const contractorsApi = {
    allContractors: () => api.get('/all_contractors'),
    projectContractors: (id) => api.get(`/projects/${id}/contractors`)
};

// -----------Suppliers API---------------------
export const suppliersApi = {
    allSuppliers: () => api.get('/all_suppliers')
};

// -----------Projects API---------------------
export const projectsApi = {
    allProjects: () => api.get('/all_projetcts'),
    getProject: (id) => api.get(`/projects/${id}`),
    newProject: (data) => api.post(`/projects`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
    updateProject: (id, data) => api.post(`/projects/${id}?_method=PATCH`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
    getProjects: (params) => api.get('/projects', { params }),
    delete: (id) => api.delete(`/projects/${id}`),

    // Stages
    newProjectStage: (data) => api.post('/projects_stages', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
    getAllProjectsStages: (params) => api.get('/projects_stages', { params }),
    getQsProjectsStages: (params) => api.get('/projects_stages/qs-projects', { params }),
    getProjectStage: (id) => api.get(`/projects_stages/${id}`),
    updateProjectStage: (id, data) => api.patch(`/projects_stages/${id}`, data),
    deleteProjectStage: (id) => api.delete(`/projects_stages/${id}`),

    // Step uploads
    uploadInstruction: (id, formData) =>
        api.post(`/project_stages/${id}/instruction`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    uploadTender: (id, formData) =>
        api.post(`/project_stages/${id}/tender`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    uploadCommunication: (id, formData) =>
        api.post(`/project_stages/${id}/communication`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    uploadCommencement: (id, formData) =>
        api.post(`/project_stages/${id}/commencement`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    uploadVariation: (id, formData) =>
        api.post(`/project_stages/${id}/variation`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

    // Progress updates (architect/dept)
    updateProgress: (id, d) => api.patch(`/projects/${id}/progress`, d),
    getProgress: (id) => api.get(`/projects/${id}/progress`),
    getArchitectProjects: (params) => api.get('/mpp_projects', { params }),
    submitToHead: (id) => api.post(`/project_stages/${id}/submit-hd`).then((r) => r.data),
    approveHd: (id, data) => api.post(`/project_stages/${id}/approve-hd`, data).then((r) => r.data),
    rejectHd: (id, data) => api.post(`/project_stages/${id}/reject-hd`, data).then((r) => r.data),
    approveQs: (id, data) => api.post(`/project_stages/${id}/approve-qs`, data).then((r) => r.data),
    rejectQs: (id, data) => api.post(`/project_stages/${id}/reject-qs`, data).then((r) => r.data)
};

// -----------Civils Dashboard API---------------------
export const CivilsDashboardApi = {};

// ─── Procurement API ──────────────────────────────────────────
export const procAPI = {
    // Dashboard
    dashboard: () => api.get('/procurement/dashboard'),
    team: () => api.get('/procurement/team'),

    // Certificates
    getCertificates: (params) => api.get('/procurement/certificates', { params }),
    getCertificate: (id) => api.get(`/procurement/certificates/${id}`),
    createCertificate: (data) => api.post('/procurement/certificates', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
    updateCertificate: (id, d) => api.put(`/procurement/certificates/${id}`, d),
    deleteCertificate: (id) => api.delete(`/procurement/certificates/${id}`),
    reviewCertificate: (id) => api.post(`/procurement/certificates/${id}/review`),
    approveCertificate: (id, d) => api.post(`/procurement/certificates/${id}/approve`, d),
    rejectCertificate: (id, d) => api.post(`/procurement/certificates/${id}/reject`, d),
    uploadCertPdf: (id, f) => {
        const fd = new FormData();
        fd.append('pdf_document', f);
        return api.post(`/procurement/certificates/${id}/upload-pdf`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },

    // Requisitions
    getRequisitions: (p) => api.get('/procurement/requisitions', { params: p }),
    getRequisition: (id) => api.get(`/procurement/requisitions/${id}`),
    createRequisition: (d) => api.post('/procurement/requisitions', d, { headers: { 'Content-Type': 'multipart/form-data' } }),
    updateRequisition: (id, formData) => api.post(`/procurement/requisitions/${id}?_method=PUT`, formData),
    deleteRequisition: (id) => api.delete(`/procurement/requisitions/${id}`),
    submitRequisition: (id) => api.post(`/procurement/requisitions/${id}/submit`),
    reviewRequisition: (id) => api.post(`/procurement/requisitions/${id}/review`),
    approveRequisition: (id, d) => api.post(`/procurement/requisitions/${id}/approve`, d),
    rejectRequisition: (id, d) => api.post(`/procurement/requisitions/${id}/reject`, d),
    technicalReviewRequisition: (id) => api.post(`/procurement/requisitions/${id}/technical-review`),
    mpcuReviewRequisition: (id) => api.post(`/procurement/requisitions/${id}/mpcu-review`),
    accountsReviewRequisition: (id) => api.post(`/procurement/requisitions/${id}/accounts-review`),
    fulfilRequisition: (id) => api.post(`/procurement/requisitions/${id}/fulfil`),

    // RFQs
    getRFQs: (p) => api.get('/procurement/rfqs', { params: p }),
    getRFQ: (id) => api.get(`/procurement/rfqs/${id}`),
    createRFQ: (d) => api.post('/procurement/rfqs', d, { headers: { 'Content-Type': 'multipart/form-data' } }),
    updateRFQ: (id, d) => api.put(`/procurement/rfqs/${id}`, d),
    deleteRFQ: (id) => api.delete(`/procurement/rfqs/${id}`),
    closeRFQ: (id) => api.post(`/procurement/rfqs/${id}/close`),
    awardRFQ: (id, d) => api.post(`/procurement/rfqs/${id}/award`, d),
    getOverdueRFQs: () => api.get('/procurement/rfqs/overdue'),
    getRFQResponses: (rfqId) => api.get(`/procurement/rfqs/${rfqId}/responses`),
    createRFQResponse: (rfqId, data) => api.post(`/procurement/rfqs/${rfqId}/responses`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
    selectRFQResponse: (rfqId, responseId) => api.post(`/procurement/rfqs/${rfqId}/responses/${responseId}/select`),
    unselectRFQResponse: (rfqId, responseId) => api.post(`/procurement/rfqs/${rfqId}/responses/${responseId}/unselect`),
    // RFQ core (existing: getRFQs, getRFQ, createRFQ, updateRFQ, deleteRFQ, closeRFQ likely already present)
    reopenRFQ: (id, payload) => api.post(`/procurement/rfqs/${id}/reopen`, payload),
    cancelRFQ: (id, reason) => api.post(`/procurement/rfqs/${id}/cancel`, { reason }),

    // Responses (existing: getRFQResponses, createRFQResponse, selectRFQResponse likely already present)
    updateRFQResponse: (rfqId, responseId, payload) => api.put(`/procurement/rfqs/${rfqId}/responses/${responseId}`, payload),
    deleteRFQResponse: (rfqId, responseId) => api.delete(`/procurement/rfqs/${rfqId}/responses/${responseId}`),

    // Evaluations / scoring
    getRFQEvaluations: (rfqId) => api.get(`/procurement/rfqs/${rfqId}/evaluations`),
    suggestPriceScores: (rfqId) => api.get(`/procurement/rfqs/${rfqId}/evaluations/suggest-price-scores`),
    saveRFQEvaluation: (rfqId, responseId, payload) => api.post(`/procurement/rfqs/${rfqId}/responses/${responseId}/evaluation`, payload),
    deleteRFQEvaluation: (rfqId, responseId) => api.delete(`/procurement/rfqs/${rfqId}/responses/${responseId}/evaluation`),

    // Purchase Orders
    getPOs: (p) => api.get('/procurement/purchase-orders', { params: p }),
    getPO: (id) => api.get(`/procurement/purchase-orders/${id}`),
    createPO: (d) => api.post('/procurement/purchase-orders', d, { headers: { 'Content-Type': 'multipart/form-data' } }),
    updatePO: (id, d) => api.put(`/procurement/purchase-orders/${id}`, d),
    deletePO: (id) => api.delete(`/procurement/purchase-orders/${id}`),
    issuePO: (id) => api.post(`/procurement/purchase-orders/${id}/issue`),
    approvePO: (id) => api.post(`/procurement/purchase-orders/${id}/approve`),
    evaluatePO: (id, d) => api.post(`/procurement/purchase-orders/${id}/evaluate`, d),
    getPOSummary: () => api.get('/procurement/purchase-orders/summary'),

    // Delivery Notes
    getDeliveries: (p) => api.get('/procurement/delivery-notes', { params: p }),
    getDelivery: (id) => api.get(`/procurement/delivery-notes/${id}`),
    createDelivery: (d) => api.post('/procurement/delivery-notes', d, { headers: { 'Content-Type': 'multipart/form-data' } }),
    updateDelivery: (id, d) => api.put(`/procurement/delivery-notes/${id}`, d),
    receiveDelivery: (id, d) => api.post(`/procurement/delivery-notes/${id}/receive`, d),
    getOverdueDeliveries: () => api.get('/procurement/delivery-notes/overdue'),

    // Invoices
    getInvoices: (p) => api.get('/procurement/invoices', { params: p }),
    getInvoice: (id) => api.get(`/procurement/invoices/${id}`),
    createInvoice: (d, isFile = false) => api.post('/procurement/invoices', d, isFile ? { headers: { 'Content-Type': 'multipart/form-data' } } : {}),
    updateInvoice: (id, d) => api.post(`/procurement/invoices/${id}`, d, { headers: { 'Content-Type': 'multipart/form-data' } }),
    reviewInvoice: (id) => api.post(`/procurement/invoices/${id}/review`),
    approveInvoice: (id) => api.post(`/procurement/invoices/${id}/approve`),
    rejectInvoice: (id, d) => api.post(`/procurement/invoices/${id}/reject`, d),
    getOverdueInvoices: () => api.get('/procurement/invoices/overdue'),

    submitToHead: (id) => api.post(`/procurement/invoices/${id}/submit-to-head`),
    approveHead: (id, data) => api.post(`/procurement/invoices/${id}/approve-head`, data),
    rejectHead: (id, data) => api.post(`procurement/invoices/${id}/reject-head`, data),
    acceptAccounting: (id, data) => api.post(`/procurement/invoices/${id}/accept-accounting`, data),
    submitToAccountingHead: (id) => api.post(`/procurement/invoices/${id}/submit-to-accounting-head`),
    approveAccounting: (id, data) => api.post(`/procurement/invoices/${id}/approve-accounting`, data),
    rejectAccounting: (id, data) => api.post(`/procurement/invoices/${id}/reject-accounting`, data),
    attachPayment: (id, data) => api.post(`/procurement/invoices/${id}/attach-payment`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),

    // Site Inspections
    getInspections: (p) => api.get('/procurement/site-inspections', { params: p }),
    getInspection: (id) => api.get(`/procurement/site-inspections/${id}`),
    createInspection: (d) => api.post('/procurement/site-inspections', d, { headers: { 'Content-Type': 'multipart/form-data' } }),
    updateInspection: (id, d) => api.put(`/procurement/site-inspections/${id}`, d),
    submitInspection: (id) => api.post(`/procurement/site-inspections/${id}/submit`),
    reviewInspection: (id) => api.post(`/procurement/site-inspections/${id}/review`),
    approveInspection: (id) => api.post(`/procurement/site-inspections/${id}/approve`),
    procTeamPerformance: (params) => api('/procurement/team-performance', { params }),
    procMemberProfile: (id, params) => api.get(`/procurement/team/${id}`, { params }),
    procMemberActivity: (id) => api.get(`/procurement/team/${id}/activity`),
};

// ─── Accounting API ───────────────────────────────────────────
export const accAPI = {
    dashboard: () => api.get('/accounting/dashboard'),
    stats: () => api.get('/accounting/stats'),
    fundSummary: () => api.get('/accounting/fund-summary'),
    team: () => api.get('/accounting/team'),
    accTeamPerformance: (params) => api('/accounting/team-performance', { params }),
    accMemberProfile: (id, params) => api.get(`/accounting/team/${id}`, { params }),
    accMemberActivity: (id) => api.get(`/accounting/team/${id}/activity`),
    getProjectDetail: (id, params) => api.get(`/accounting/projects/${id}`, { params }),

    // Certificates
    getCertificates: (p) => api.get('/accounting/certificates', { params: p }),
    updateCertStatus: (id, status) => api.put(`/accounting/certificates/${id}/status`, { status }),
    attachVoucher: (id, file) => {
        const fd = new FormData();
        fd.append('payment_voucher', file);
        return api.post(`/accounting/certificates/${id}/attach-voucher`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },

    // Payments
    getPayments: (p) => api.get('/accounting/payments', { params: p }),
    getPendingPayments: () => api.get('/accounting/payments/pending'),
    getConfirmedPayments: (p) => api.get('/accounting/payments/confirmed', { params: p }),
    confirmPayment: (id, d) => api.post(`/accounting/payments/${id}/confirm`, d),
    createPayment: (d) => api.post('/accounting/payments', d),

    // Expenditures
    getExpenditures: (p) => api.get('/accounting/expenditures', { params: p }),
    createExpenditure: (d) => api.post('/accounting/expenditures', d),
    updateExpenditure: (id, d) => api.put(`/accounting/expenditures/${id}`, d),
    deleteExpenditure: (id) => api.delete(`/accounting/expenditures/${id}`),
    byFund: () => api.get('/accounting/expenditures/by-fund'),
    byRegion: () => api.get('/accounting/expenditures/by-region'),
    byDept: () => api.get('/accounting/expenditures/by-department'),

    // Transfers
    getTransfers: (p) => api.get('/accounting/transfers', { params: p }),
    createTransfer: (d) => api.post('/accounting/transfers', d),
    approveTransfer: (id) => api.post(`/accounting/transfers/${id}/approve`),
    rejectTransfer: (id, d) => api.post(`/accounting/transfers/${id}/reject`, d),
    getTransfer: (id) => api.get(`/accounting/transfers/${id}`),
    deleteTransfer: (id) => api.delete(`/accounting/transfers/${id}`),
    getSectorTransfers: (p) => api.get('/accounting/sector-transfers', { params: p }),
    createSectorTransfer: (d) => api.post('/accounting/sector-transfers', d),
    approveSectorTransfer: (id) => api.post(`/accounting/sector-transfers/${id}/approve`),
    rejectSectorTransfer: (id, d) => api.post(`/accounting/sector-transfers/${id}/reject`, d),
    getSectorTransfer: (id) => api.get(`/accounting/sector-transfers/${id}`),
    deleteSectorTransfer: (id) => api.delete(`/accounting/sector-transfers/${id}`),
    sectorBudgets: (id) => api.get(`/accounting/sector-budgets`),

    // Reports
    fundUtilization: (p) => api.get('/accounting/reports/fund-utilization', { params: p }),
    paymentHistory: (p) => api.get('/accounting/reports/payment-history', { params: p }),
    certsDue: () => api.get('/accounting/reports/certificates-due')
};

// ─── Shared API ────────────────────────────────────────────────
export const sharedAPI = {
    getFunds: () => api.get('/funds'),
    getProjects: () => api.get('/projects'),
    getSuppliers: () => api.get('/suppliers'),
    getContractors: () => api.get('/contractors'),
    getNotifications: (p) => api.get('/notifications', { params: p }),
    markRead: (id) => api.put(`/notifications/${id}/read`),
    markAllRead: () => api.put('/notifications/read-all')
};

// ── Budget Categories ──────────────────────────────────────
export const budgetApi = {
    getByProject: (projectId) => api.get(`/project_stages/${projectId}/budget-categories`),
    create: (projectId, data) => api.post(`/project_stages/${projectId}/budget-categories`, data),
    update: (id, data) => api.put(`/budget-categories/${id}`, data),
    delete: (id) => api.delete(`/budget-categories/${id}`),
    updateProgress: (id, d) => api.put(`/budget-categories/${id}/progress`, d)
};

// ── Claims ─────────────────────────────────────────────────
export const claimsApi = {
    allClaims: (params) => api.get('/claims', { params }).then((r) => r.data),
    getByProject: (projectId, p) => api.get(`/project_stages/${projectId}/claims`, { params: p }).then((r) => r.data.data),
    getOne: (id) => api.get(`/claims/${id}`),
    create: (projectId, formData) =>
        api.post(`/project_stages/${projectId}/claims`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    update: (id, formData) => api.post(`/claims/${id}?_method=PUT`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    delete: (id) => api.delete(`/claims/${id}`)
};

// ── Certificates ───────────────────────────────────────────
export const certificatesApi = {
    getAll: (params) => api.get('/certificates', { params }).then((r) => r.data),
    getByProject: (projectId) => api.get(`/project_stages/${projectId}/certificates`).then((r) => r.data),
    getOne: (id) => api.get(`/certificates/${id}`).then((r) => r.data),
    create: (projectId, formData) =>
        api
            .post(`/project_stages/${projectId}/certificates`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
            .then((r) => r.data),
    update: (id, formData) =>
        api.post(`/certificates/${id}?_method=PUT`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data),
    delete: (id) => api
        .delete(`/certificates/${id}`)
        .then((r) => r.data)
        .then((r) => r.data),

    // Workflow actions
    submitToQsHead: (id) => api.post(`/certificates/${id}/submit-qs`).then((r) => r.data),
    submitToProcurementHead: (id) => api.post(`/certificates/${id}/submit-procurement`).then((r) => r.data),
    submitToAccountingHead: (id) => api.post(`/certificates/${id}/submit-accounting`).then((r) => r.data),
    approveQs: (id, data) => api.post(`/certificates/${id}/approve-qs`, data).then((r) => r.data),
    rejectQs: (id, data) => api.post(`/certificates/${id}/reject-qs`, data).then((r) => r.data),
    approveProcurement: (id, data) => api.post(`/certificates/${id}/approve-procurement`, data).then((r) => r.data),
    assignProcurement: (id, data) => api.post(`/certificates/${id}/accept-procurement`, data).then((r) => r.data),
    rejectProcurement: (id, data) => api.post(`/certificates/${id}/reject-procurement`, data).then((r) => r.data),
    approveAccounting: (id, data) => api.post(`/certificates/${id}/approve-accounting`, data).then((r) => r.data),
    assignAccounting: (id, data) => api.post(`/certificates/${id}/accept-accounting`, data).then((r) => r.data),
    approveChecking: (id) => api.post(`/certificates/${id}/approve-checking`).then((r) => r.data),
    rejectAccounting: (id, data) => api.post(`/certificates/${id}/reject-accounting`, data).then((r) => r.data),
    submitTreasury: (id) => api.post(`/certificates/${id}/submit-treasury`).then((r) => r.data),
    attachVoucher: (id, formData) =>
        api.post(`/certificates/${id}/voucher`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data),
    markPaid: (id, data) => api.post(`/certificates/${id}/mark-paid`, data).then((r) => r.data),
    getChecklist: (certificateId) => api.get(`/certificates/${certificateId}/checklist`),

    updateChecklist: (certificateId, payload) => api.patch(`/certificates/${certificateId}/checklist`, payload),

    resetChecklist: (certificateId) => api.post(`/certificates/${certificateId}/checklist/reset`),

};

// ── Dashboard ──────────────────────────────────────────────
export const qsDashboardApi = {
    getStats: () => api.get('/qs-dashboard/stats'),
    myDashboard: () => api.get('/qs/officer/stats'),
    getRecentActivity: () => api.get('/qs-dashboard/activity'),
    getCertificateStatusSummary: () => api.get('/qs-dashboard/certificate-status'),
    teamPerformance: () => api.get('/qs-dashboard/team-performance'),
    team: () => api.get('/qs-dashboard/team'),
    qsTeamPerformance: () => api.get('/qs-dashboard/team-performance'),
    qsMemberProfile: (id) => api.get(`/qs-dashboard/team/${id}`),
    qsMemberActivity: (id) => api.get(`/qs-dashboard/team/${id}/activity`)
};

// ── Reports ────────────────────────────────────────────────
export const qsReportsApi = {
    getBudgetUtilization: (params) => api.get('/qs-reports/budget-utilization', { params }),
    getCertificateReport: (params) => api.get('/qs-reports/certificates', { params })
};

const upload = (cfg) => ({ ...cfg, headers: { 'Content-Type': 'multipart/form-data' } });

// ── Site Inspections ──────────────────────────────────────────────────────
export const inspectionsApi = {
    getAll: (p) => api.get('/reports', { params: p }),
    mppReports: (p) => api.get('/mpp_reports', { params: p }),
    getByProject: (pid, p) => api.get(`/project/${pid}/reports`, { params: p }),
    getOne: (id) => api.get(`/reports/${id}`),
    create: (fd) => api.post(`/reports`, fd, upload({})),
    update: (id, fd) => api.post(`/reports/${id}?_method=PUT`, fd, upload({})),
    delete: (id) => api.delete(`/reports/${id}`)
};

// ── Site Meetings ─────────────────────────────────────────────────────────
export const meetingsApi = {
    getAll: (p) => api.get('/meetings', { params: p }),
    mppMeetings: (p) => api.get('/mpp_meetings', { params: p }),
    getByProject: (pid, p) => api.get(`/projects/${pid}/meetings`, { params: p }),
    getOne: (id) => api.get(`/meetings/${id}`),
    create: (pid, fd) => api.post(`/projects/${pid}/meetings`, fd, upload({})),
    update: (id, fd) => api.post(`/meetings/${id}?_method=PUT`, fd, upload({}))
};

// ── Architecture Dashboard ────────────────────────────────────────────────
export const architectApi = {
    dashboard: () => api.get('/architect/dashboard'),
    myDashboard: () => api.get('/architect/my-dashboard'),
    teamPerformance: () => api.get('/architect/team-performance'),
    team: () => api.get('/architect/team'),
    projectFundHealth: () => api.get('/architect/fund-health'),
    memberProfile: (id) => api.get(`/architect/members/${id}`),
    memberActivity: (id) => api.get(`/architect/members/${id}/activity`),
    downloadReport: (p) => api.get('/architect/reports/download', { params: p, responseType: 'blob' })
};



export const tasksApi = {
    list: (projectId, params) => api.get(`projects/${projectId}/tasks`, { params }),
    create: (projectId, data) => api.post(`projects/${projectId}/tasks`, data),
    update: (projectId, id, data) => api.put(`projects/${projectId}/tasks/${id}`, data),
    delete: (projectId, id) => api.delete(`projects/${projectId}/tasks/${id}`),
};
